import { useEffect, useState } from 'react';
import { printingApi } from '@verlo/shared/services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './Section';
import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import { Label } from '@verlo/shared/components/ui/label';
import { Switch } from '@verlo/shared/components/ui/switch';
import { Segmented, OptionChips } from '@verlo/shared/components/ui/segmented';
import { Notice } from '@verlo/shared/components/ui/notice';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from '@verlo/shared/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Save, Trash2 } from 'lucide-react';

// The venue's printers (#94). A ticket goes to the printer whose role matches its station,
// so this is the routing table: what each machine is for, where it is on the network, and
// what it can do. The till dials them itself — the office only says which.

const ROLES = [
  { value: 'kitchen', label: 'Kitchen', hint: 'food tickets, as they are fired' },
  { value: 'bar', label: 'Bar', hint: 'drinks tickets' },
  { value: 'receipt', label: 'Receipt', hint: "a guest's bill, when they ask" },
];
const KINDS = [
  { value: 'impact', label: 'Impact', hint: 'dot matrix: survives kitchen heat, prints red, no cutter' },
  { value: 'thermal', label: 'Thermal', hint: 'the usual receipt printer: cuts, opens a drawer' },
];
const BLANK = { name: '', role: 'kitchen', kind: 'impact', host: '', port: 9100, stations: [], copies: 1, is_active: true, notes: '' };

const PrintersCard = () => {
  const [rows, setRows] = useState(null);
  const [stations, setStations] = useState([]);
  const [editing, setEditing] = useState(null);     // null | BLANK | row
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);

  const load = () => {
    printingApi.printers()
      .then(({ data }) => setRows(data || []))
      .catch(() => toast.error('Could not load the printers'));
    printingApi.stations().then(({ data }) => setStations(data.stations || [])).catch(() => {});
  };
  useEffect(load, []);

  const save = async () => {
    // The hardware address is left to the cloud: it keeps it, or forgets it when the
    // address on the network changes. Sending the row's copy back would keep a stale one.
    const { mac: _mac, ...fields } = form;
    const payload = {
      ...fields,
      port: Number(form.port) || 9100,
      copies: Number(form.copies) || 1,
      stations: form.role === 'kitchen' ? form.stations : [],
    };
    if (!payload.name.trim()) { toast.error('A printer needs a name'); return; }
    if (!payload.host.trim()) { toast.error('A printer needs an address on the network'); return; }
    setSaving(true);
    try {
      if (editing?.id) await printingApi.updatePrinter(editing.id, payload);
      else await printingApi.createPrinter(payload);
      toast.success(editing?.id ? 'Printer saved' : `${payload.name} added`);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save the printer");
    } finally { setSaving(false); }
  };

  // Removing a printer stops a station's tickets reaching paper, and the row carries a
  // host and port somebody typed off the back of a machine. One tap was all it took (#126).
  const [removing, setRemoving] = useState(null);

  const remove = async (row) => {
    setRemoving(null);
    try { await printingApi.deletePrinter(row.id); toast.success(`${row.name} removed`); load(); }
    catch { toast.error("Couldn't remove it"); }
  };

  if (rows === null) return null;

  return (
    <Card data-testid="settings-printers">
      <CardHeader>
        <CardTitle>Printers</CardTitle>
        <CardDescription>
          Where a ticket goes when it is fired. The kitchen's and the bar's print themselves;
          a receipt prints when a guest asks. Each one is a machine on the restaurant's own
          network, so the till reaches them with the internet down, which is also why
          whether one is <em>working</em> is something only the till can say. Test a printer,
          and see what is stuck, on the till itself.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {rows.length === 0 && (
          <Notice tone="warn" word="None">
            No printer is set up, so nothing reaches the kitchen on paper. Tickets still
            appear on the kitchen screen.
          </Notice>
        )}

        <div className="divide-y divide-hairline border border-hairline bg-white">
          {rows.map((row) => {
            return (
              <div key={row.id} className="flex flex-wrap items-center gap-3 px-3 py-2.5" data-testid={`printer-row-${row.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-[14px] font-semibold text-ink">
                    {row.name}
                    {!row.is_active && <span className="ml-2 text-[12px] font-medium text-ink-faint">switched off</span>}
                  </p>
                  <p className="text-[12.5px] text-ink-soft">
                    {ROLES.find((r) => r.value === row.role)?.label} · {row.kind} · {row.width} columns
                    {row.stations?.length ? ` · ${row.stations.join(', ')}` : ''}
                    {row.copies > 1 ? ` · ${row.copies} copies` : ''}
                  </p>
                  <p className="font-mono text-[12px] text-ink-faint tabular-nums">{row.host}:{row.port}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="ghost" className="h-8" onClick={() => { setForm({ ...BLANK, ...row }); setEditing(row); }} data-testid={`printer-edit-${row.id}`}>Edit</Button>
                  <Button size="sm" variant="ghost" className="h-8 text-signal-stop" onClick={() => setRemoving(row)}
                          title={`Remove ${row.name}`} data-testid={`printer-remove-${row.id}`}><Trash2 size={13} /></Button>
                </div>
              </div>
            );
          })}
        </div>

        {editing ? (
          <div className="border border-hairline border-l-[3px] border-l-verlo bg-surface-2 p-3 space-y-3" data-testid="printer-form">
            <div className="grid gap-3 sm:grid-cols-[1fr_1fr]">
              <div className="grid gap-1.5">
                <Label className="text-xs">Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                       placeholder="e.g. Kitchen" data-testid="printer-name" autoFocus />
              </div>
              <div className="grid gap-1.5">
                <Label className="text-xs">Address on the network</Label>
                <div className="flex gap-2">
                  <Input value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })}
                         placeholder="e.g. 192.168.1.50" className="font-mono tabular-nums" data-testid="printer-host" />
                  <Input type="number" value={form.port} onChange={(e) => setForm({ ...form, port: e.target.value })}
                         className="w-24 font-mono tabular-nums" data-testid="printer-port" />
                </div>
                <p className="text-[11.5px] text-ink-faint">Port 9100 unless the printer says otherwise.</p>
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="grid gap-1.5">
                <Label className="text-xs">What it prints</Label>
                <Segmented value={form.role} onChange={(v) => setForm({ ...form, role: v })}
                           options={ROLES.map((r) => ({ value: r.value, label: r.label, testId: `printer-role-${r.value}` }))} stretch />
                <p className="text-[11.5px] text-ink-faint">{ROLES.find((r) => r.value === form.role)?.hint}</p>
              </div>
              <div className="grid gap-1.5">
                <Label className="text-xs">What it is</Label>
                <Segmented value={form.kind} onChange={(v) => setForm({ ...form, kind: v })}
                           options={KINDS.map((k) => ({ value: k.value, label: k.label, testId: `printer-kind-${k.value}` }))} stretch />
                <p className="text-[11.5px] text-ink-faint">{KINDS.find((k) => k.value === form.kind)?.hint}</p>
              </div>
            </div>
            {form.role === 'kitchen' && stations.length > 1 && (
              <div className="grid gap-1.5">
                <Label className="text-xs">Only these stations</Label>
                <OptionChips
                  multiple
                  options={stations.filter((s) => s.key !== 'kitchen').map((s) => ({ value: s.key, label: s.label, testId: `printer-station-${s.key}` }))}
                  value={form.stations}
                  onChange={(v) => setForm({ ...form, stations: v })}
                />
                <p className="text-[11.5px] text-ink-faint">None picked: this printer takes every food ticket, which is the usual one-printer kitchen.</p>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-6">
              <label className="flex items-center gap-2 text-[13.5px]">
                Copies
                <Input type="number" min="1" max="5" value={form.copies}
                       onChange={(e) => setForm({ ...form, copies: e.target.value })}
                       className="h-8 w-16 font-mono tabular-nums" data-testid="printer-copies" />
              </label>
              <label className="flex items-center gap-3 text-[13.5px]">
                Switched on
                <Switch checked={form.is_active !== false} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="printer-active" />
              </label>
            </div>
            <div className="flex gap-2">
              <Button className="bg-verlo hover:bg-verlo-hover" onClick={save} disabled={saving} data-testid="printer-save">
                <Save size={14} className="mr-1.5" />{editing?.id ? 'Save' : 'Add printer'}
              </Button>
              <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}>Cancel</Button>
            </div>
          </div>
        ) : (
          <Button variant="outline" onClick={() => { setForm(BLANK); setEditing(BLANK); }} data-testid="printer-add">
            <Plus size={14} className="mr-1.5" />Add a printer
          </Button>
        )}
      </CardContent>

      <AlertDialog open={Boolean(removing)} onOpenChange={(o) => !o && setRemoving(null)}>
        <AlertDialogContent data-testid="printer-remove-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {removing?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              {removing?.role === 'receipt'
                ? 'Guests who ask for a printed receipt will be told there is no receipt printer.'
                : `${removing?.role === 'bar' ? 'Drinks' : 'Food'} tickets will stop reaching this printer. They stay on the kitchen screen.`}
              {' '}You will need its address ({removing?.host}:{removing?.port || 9100}) to add it back.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction className="bg-signal-stop hover:bg-signal-stop"
                               onClick={() => remove(removing)} data-testid="printer-remove-confirmed">
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default PrintersCard;
